import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '@/lib/adminApi';
import { Lock, Shield, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [password, setPassword] = useState('');
  const [answer, setAnswer] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('brandos_admin_token')) {
      navigate('/skyportal/dashboard', { replace: true });
      return;
    }
    adminAPI.getSecurityQuestion().then(r => setQuestion(r.data.question)).catch(() => {});
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || !answer) return toast.error('Both fields required');
    setLoading(true);
    try {
      const res = await adminAPI.login({ password, security_answer: answer });
      localStorage.setItem('brandos_admin_token', res.data.token);
      toast.success('Welcome back, Admin');
      navigate('/skyportal/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #0f0c1d 0%, #1a1233 50%, #0a0518 100%)' }} data-testid="admin-login-page">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full opacity-30" style={{ background: 'radial-gradient(circle, #a855f7 0%, transparent 70%)' }} />
        <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #ec4899 0%, transparent 70%)' }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
        className="relative w-full max-w-md"
      >
        <div className="bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-3xl p-8 shadow-2xl">
          <div className="flex flex-col items-center mb-8">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'linear-gradient(145deg, #a855f7, #6d28d9)', boxShadow: '0 8px 32px rgba(168,85,247,0.5)' }}>
              <Shield className="w-7 h-7 text-white" strokeWidth={2} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>Sky Portal</h1>
            <p className="text-sm text-purple-200/60">Authorized access only</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-purple-200/70 mb-2 font-semibold">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-300/50" />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-12 py-3 text-white placeholder-purple-200/30 focus:outline-none focus:border-purple-400/50 transition"
                  placeholder="Enter admin password"
                  data-testid="admin-password-input"
                  autoFocus
                />
                <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-300/50 hover:text-white">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-purple-200/70 mb-2 font-semibold">Security Question</label>
              <p className="text-sm text-white/80 mb-2 italic">{question || 'Loading…'}</p>
              <input
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-purple-200/30 focus:outline-none focus:border-purple-400/50 transition"
                placeholder="Your answer"
                data-testid="admin-answer-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl font-semibold text-white transition disabled:opacity-50"
              style={{ background: 'linear-gradient(145deg, #a855f7, #7c3aed)', boxShadow: '0 8px 24px rgba(168,85,247,0.4)' }}
              data-testid="admin-login-submit"
            >
              {loading ? 'Verifying…' : 'Enter Portal'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-purple-200/30 mt-4">
          Brand OS · Sky Portal v1.0
        </p>
      </motion.div>
    </div>
  );
}
